import { pvpRosterSize } from '@kenocyber/shared';

/**
 * **O Coração Vital: um feixe verde entre dois cascos, e um brilho na própria
 * silhueta** (gate V28G.U).
 *
 * ## Quem manda desenhar, e quem nunca manda
 *
 * Este módulo desenha uma CURA que a autoridade já confirmou. Ele não sabe o que
 * é um perfil, um socket, uma cópia, um nível, uma chance ou uma fórmula, e não
 * tem por onde saber: as suas entradas são duas posições, dois raios e uma
 * silhueta - a textura que a cena já usa para o casco. Não infere nada pela
 * vida, pela barra nem pelo que a nave tem equipada.
 *
 * ## Duas formas, e a razão de serem duas
 *
 * Uma cura de OUTRA nave tem duas pontas, e o feixe é o que as liga. Uma
 * AUTOCURA não tem duas: desenhar um feixe de comprimento zero seria desenhar
 * nada, e inventar-lhe uma direção seria mentir sobre para onde a energia foi.
 * Por isso a autocura é um brilho na silhueta e nunca um feixe, e a cura de um
 * aliado é um feixe e nunca um brilho no curador - quem foi curado é o outro.
 *
 * ## Porque o feixe começa e acaba nas BORDAS
 *
 * Porque o contrato o exige - *«o feixe deve começar e terminar nas bordas reais
 * dos cascos, respeitando a escala de cada nave»* - e porque um casco de duelo
 * é escalado pela vida máxima desde o gate V28G.S. Uma linha de centro a centro
 * atravessaria uma nave grande por dentro, e a energia pareceria nascer lá
 * dentro em vez de sair dela.
 *
 * O corte acontece AQUI e não na cena, porque é uma lei do efeito e não uma
 * conveniência de quem chama: `vitalBeamSegment` é pura, e as provas medem-na
 * sem um contexto WebGL.
 *
 * ## Porque não parece um laser ofensivo
 *
 * Porque não é um laser: aparece inteiro, apaga em pouco mais de cem
 * milissegundos, é fino, tem núcleo quase branco com halo verde-neon, e não
 * viaja. A regra permanente do `CLAUDE.md` é sobre CADÊNCIA - nenhuma sequência
 * de tiros pode virar um feixe contínuo - e isto não substitui tiro nenhum: o
 * projétil comum sai da mesma maneira, com a mesma arte, no mesmo quadro.
 *
 * ## E porque a tira é uma TEXTURA
 *
 * Porque um `lineBetween` reconstrói geometria em cada quadro que vive, e num
 * 3x3 pode haver várias curas ao mesmo tempo. Uma tira é uma textura criada UMA
 * vez e esticada entre dois pontos - uma imagem, uma rotação, uma escala - e não
 * aloca nada enquanto corre. É a mesma escolha de `reflect-flash.ts`, pela mesma
 * razão.
 *
 * ## Nada aqui é aleatório
 *
 * Não há `Math.random()` em lado nenhum, e não há variação por evento. Duas
 * pessoas a ver a mesma cura veem exatamente a mesma coisa, porque a única
 * entrada que muda é a geometria que a autoridade decidiu.
 */

/* -------------------------------------------------------------------------- */
/*  O tempo                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * **A vida de um feixe: 120 ms.**
 *
 * Dentro da banda 100–150 ms que o gate manda construir primeiro, e no meio dela
 * de propósito: é a distância máxima das duas pontas, portanto um ajuste de
 * ritmo numa direção ou noutra não sai da banda. A aprovação final é manual, e
 * este número é o ponto de partida dela.
 *
 * Mais longo que os 72 ms da Reflexão, e é deliberado: aquilo é um relâmpago de
 * defesa e isto é uma transferência. O olho tem de seguir a energia de uma nave
 * até à outra, e setenta e dois milissegundos numa arena larga leem-se como um
 * pisca.
 */
export const VITAL_BEAM_DURATION_MS = 120;

/** A vida do brilho da autocura: a mesma, e pela mesma razão que a Reflexão a partilha. */
export const VITAL_GLOW_DURATION_MS = 120;

/**
 * Quando o brilho atinge o máximo: 18 ms.
 *
 * Cedo, mas não instantâneo. Um brilho que nascesse no máximo pisca; uma subida
 * curta é a diferença entre uma recuperação e um erro de quadro.
 */
export const VITAL_GLOW_PEAK_MS = 18;

/** E quando o feixe atinge o máximo. Mais cedo ainda: ele APARECE. */
export const VITAL_BEAM_PEAK_MS = 10;

/* -------------------------------------------------------------------------- */
/*  A forma                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * **O halo da autocura ENCOLHE, e o da Reflexão cresce.**
 *
 * É a diferença de leitura que o contrato pede - *«o brilho precisa de ler como
 * recuperação, não como Égide ou Reflexão»* - e ela não é a cor sozinha.
 *
 * Um halo que se expande é energia a SAIR: é o que a Égide faz, e o que uma onda
 * de choque faz. Um halo que nasce ligeiramente maior e assenta sobre a silhueta
 * é energia a ENTRAR, que é exatamente o que uma cura é. A cor diz "verde"; o
 * sentido do movimento diz "recebeu".
 */
export const VITAL_GLOW_HALO_START_SCALE = 1.16;

/** O alpha máximo do núcleo. Verde quase pleno, e nunca opaco. */
export const VITAL_GLOW_CORE_ALPHA = 0.85;

/** O alpha máximo do halo. Uma fração do núcleo: ele é o eco, não o brilho. */
export const VITAL_GLOW_HALO_ALPHA = 0.34;

/**
 * A espessura desenhada do feixe, em píxeis da arena: **6**.
 *
 * Mais fino do que os 7 da Reflexão. *«Feixe muito rápido»* e *«núcleo fino»*
 * são decisões de leitura: um feixe grosso a atravessar a arena lê-se como um
 * laser contínuo, que é o que o `CLAUDE.md` proíbe para toda a cadência do jogo
 * e o que este contrato proíbe para este efeito.
 */
export const VITAL_BEAM_THICKNESS_PX = 11;

/**
 * A espessura do HALO exterior, por baixo do núcleo: **26 px**.
 *
 * Ele é o que dá profundidade ao feixe sem uma única partícula: uma faixa larga,
 * muito fraca e puramente verde, que apaga mais depressa do que o núcleo. O que
 * o olho lê é energia à volta de um fio, e não um fio sozinho.
 *
 * Larga e FRACA, nesta ordem: um halo estreito lê-se como um segundo traço, e
 * um halo forte lê-se como o laser contínuo que o contrato proíbe.
 */
export const VITAL_BEAM_GLOW_THICKNESS_PX = 26;

/** O comprimento da textura da tira. Ela é ESTICADA; isto só decide a nitidez. */
const BEAM_TEXTURE_LENGTH = 96;

/**
 * **Verde-neon**, a cor da energia. **O mesmo verde do `+TOTAL` aprovado.**
 *
 * Era `0x3dff7a` - um verde de menta, mais claro e menos saturado - e lia-se
 * apagado ao lado do número que aparece por cima da nave. Passou a ser
 * exatamente `#39ff14`, que é o que `combat-text.ts` já usa para a cura.
 *
 * Uma cura tem uma cor, e não duas parecidas. O módulo não importa o valor
 * para se manter uma folha - a prova é que os liga, e falha se divergirem.
 *
 * Escrita aqui uma vez e usada nas três camadas que a precisam: o halo da tira,
 * o núcleo do brilho e o halo do brilho.
 */
export const VITAL_NEON_GREEN = 0x39ff14;

/**
 * **Branco esverdeado**, a cor do NÚCLEO do feixe.
 *
 * Quase branco e não branco: um núcleo branco puro dentro de um halo verde lê-se
 * como dois efeitos empilhados. Este é a mesma luz, mais quente no meio.
 */
export const VITAL_CORE_WHITE = 0xeafff2;

/**
 * Verde quente, entre o núcleo e o neon. É ele que faz o fio LER como verde.
 *
 * Era um verde de menta (`0x8bffb0`), quase pastel: somado em ADD sobre o
 * núcleo, empurrava o conjunto para branco em vez de o puxar para verde. Este
 * é saturado, e é o que dá o filamento quente de um tubo de néon.
 */
export const VITAL_BRIGHT_GREEN = 0x9dff3d;

/**
 * Verde profundo, para as bandas de fora e para o halo.
 *
 * Mais claro do que era (`0x1fcf5a`), e a razão é o blend: em ADD a cor é
 * SOMADA ao que está por baixo, portanto um verde escuro quase não acrescenta
 * nada - ele não escurece as bordas, apenas desaparece.
 */
export const VITAL_DEEP_GREEN = 0x23f03a;

/**
 * As bandas da tira, do centro para fora: **alpha e cor por linha de píxeis**.
 *
 * O núcleo é fino - duas linhas quase brancas - e o resto é verde a apagar.
 * Escritas como uma tabela e não como uma fórmula porque são uma decisão de
 * arte, e uma fórmula convidaria alguém a "melhorá-la" sem olhar para o
 * resultado.
 */
const BEAM_BANDS: readonly { readonly alpha: number; readonly color: number }[] = Object.freeze([
  /*
   * O núcleo DESCE e o verde ao lado dele SOBE, e a inversão é deliberada.
   *
   * O que se quer é um filamento quente dentro de um tubo verde, e não uma
   * linha branca com verde à volta. A banda mais forte do feixe passa a ser
   * verde; a branca é fina e existe só para dar o ponto quente.
   */
  { alpha: 0.88, color: VITAL_CORE_WHITE },
  { alpha: 0.95, color: VITAL_BRIGHT_GREEN },
  { alpha: 0.86, color: VITAL_NEON_GREEN },
  { alpha: 0.6, color: VITAL_NEON_GREEN },
  { alpha: 0.34, color: VITAL_DEEP_GREEN },
  { alpha: 0.15, color: VITAL_DEEP_GREEN },
]);

/**
 * **A FORMA ao comprimento: pontas afiladas e nós de energia.**
 *
 * A primeira versão tinha bandas de comprimento inteiro e alpha constante, e a
 * consequência foi a que o dono relatou: *«parece mais uma linha»*. Uma faixa
 * uniforme É uma linha - não tem nada que o olho possa seguir do curador até ao
 * curado.
 *
 * Duas coisas resolvem isso sem uma única partícula:
 *
 * - **as pontas afilam.** Um traço que começa e acaba a direito lê-se como um
 *   segmento cortado; um que nasce e morre lê-se como energia;
 * - **os NÓS.** Seis cristas estreitas ao longo do feixe, que lhe dão direção e
 *   textura. São função de `t` e de mais nada - a mesma forma para toda a gente,
 *   sem `Math.random()`, porque duas pessoas têm de ver a mesma cura.
 */
/**
 * As bandas do HALO, do centro para fora. **Nenhuma delas é branca.**
 *
 * Sete linhas muito fracas, esticadas para vinte e seis píxeis: o que sai é um
 * volume difuso, e não um segundo traço. O centro é neon e o resto é o verde
 * profundo, para o halo escurecer ao afastar-se em vez de apenas desaparecer.
 */
const GLOW_BANDS: readonly number[] = Object.freeze([0.3, 0.24, 0.17, 0.11, 0.06, 0.03, 0.015]);

function beamLengthShape(t: number): number {
  /* As pontas: quinze por cento de cada lado, em curva e não em rampa. */
  const edge = Math.min(t, 1 - t) / 0.15;
  const taper = edge >= 1 ? 1 : edge * edge * (3 - 2 * edge);
  /* Os nós: cristas estreitas, porque uma onda larga voltaria a ser uma linha. */
  const wave = Math.max(0, Math.sin(t * Math.PI * 6));
  return taper * (0.72 + 0.28 * wave ** 5);
}

/* -------------------------------------------------------------------------- */
/*  Os tetos, derivados                                                       */
/* -------------------------------------------------------------------------- */

/** Quantos cascos uma sala pode ter. Lido, nunca escrito. */
const HULLS = pvpRosterSize('team_3v3');

/**
 * **Quantos brilhos podem estar vivos: um por casco.**
 *
 * DERIVADO, e a derivação é uma decisão de desenho: um casco tem UM brilho, e
 * uma segunda autocura dentro dos 120 ms REINICIA o dele em vez de acender um
 * segundo. Dois brilhos idênticos na mesma silhueta não são dois brilhos - são
 * um brilho mais claro, e o jogador não conseguiria contá-los. Quem diz que
 * houve duas curas é o `+TOTAL`, que soma.
 */
export const VITAL_GLOW_POOL = HULLS;

/**
 * **Quantos feixes podem estar vivos: um por PAR ordenado de cascos.**
 *
 * `6 x 5 = 30`. Um feixe liga dois cascos, portanto o que o identifica é o par -
 * e um segundo feixe entre os mesmos dois dentro dos 120 ms reinicia o primeiro,
 * porque desenharia exatamente a mesma linha.
 *
 * `HULLS - 1` e não `HULLS`: uma autocura não desenha feixe nenhum, e o par
 * `x -> x` não existe.
 */
export const VITAL_BEAM_POOL = HULLS * (HULLS - 1);

/**
 * **Quantos slots nascem PRONTOS, na abertura da arena.**
 *
 * Seis de cada, e o número é derivado do pior instante possível: um tique
 * produz no máximo uma cura por nave, portanto seis cascos produzem no máximo
 * seis apresentações ao mesmo tempo. Elas podem ser seis feixes, seis brilhos
 * ou qualquer mistura - e é por isso que são seis de CADA, e não seis ao todo.
 *
 * ## Porque isto existe
 *
 * Porque o pool nascia vazio e a PRIMEIRA cura pagava a criação de tudo. A
 * medição por fases datou-o ao milissegundo: a rajada saiu aos 2 000 ms, o
 * pior quadro foi aos 2 050 ms, e é nesse quadro que a lista de desenho salta
 * de 60 para 78 objetos. Quarenta e cinco milissegundos e meio, contra 17,8 na
 * linha de base e 19,0 em regime - onde quarenta rajadas não movem a contagem.
 *
 * A abertura da arena já cria cascos, canhões, barras e marcadores. Alguns
 * sprites invisíveis a mais nesse instante são ruído; o mesmo custo no primeiro
 * quadro de combate é um travão que se vê.
 *
 * Os TETOS não mudam: um sétimo slot continua a nascer a pedido, até
 * {@link VITAL_GLOW_POOL} e {@link VITAL_BEAM_POOL}. Isto adianta o custo, e
 * não o limita.
 */
export const VITAL_PREWARMED_SLOTS = HULLS;

/* -------------------------------------------------------------------------- */
/*  A geometria, pura                                                         */
/* -------------------------------------------------------------------------- */

/** Um segmento entre duas BORDAS, ou `null` quando não há nada a desenhar. */
export interface VitalBeamSegment {
  readonly fromX: number;
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
  readonly length: number;
  readonly rotation: number;
}

/**
 * **O feixe recortado às bordas dos dois cascos.** Pura, e por isso provável.
 *
 * Devolve `null` quando não há segmento honesto a desenhar:
 *
 * - os dois centros no mesmo ponto - geometricamente impossível entre corpos
 *   sólidos, e sem direção não há linha que não seja inventada;
 * - os dois cascos encostados ou sobrepostos, ou seja `raioA + raioB >= dist`.
 *   Aí não há espaço ENTRE eles, e uma linha desenhada nesse espaço apareceria
 *   ao contrário, atravessando os dois por dentro. O `+TOTAL` continua a
 *   aparecer, que é o que diz ao jogador que a cura aconteceu.
 *
 * Nunca satura, nunca arredonda para uma linha mínima e nunca desloca uma ponta
 * para caber: uma linha que não pode ser desenhada honestamente não é desenhada.
 */
export function vitalBeamSegment(input: {
  readonly fromX: number;
  readonly fromY: number;
  readonly fromRadius: number;
  readonly toX: number;
  readonly toY: number;
  readonly toRadius: number;
}): VitalBeamSegment | null {
  const dx = input.toX - input.fromX;
  const dy = input.toY - input.fromY;
  const distance = Math.hypot(dx, dy);
  if (!Number.isFinite(distance) || distance <= 0) {
    return null;
  }
  const from = Math.max(0, input.fromRadius);
  const to = Math.max(0, input.toRadius);
  const span = distance - from - to;
  if (!(span > 0)) {
    return null;
  }
  const ux = dx / distance;
  const uy = dy / distance;
  return Object.freeze({
    fromX: input.fromX + ux * from,
    fromY: input.fromY + uy * from,
    toX: input.toX - ux * to,
    toY: input.toY - uy * to,
    length: span,
    rotation: Math.atan2(dy, dx),
  });
}

/* -------------------------------------------------------------------------- */
/*  A cena, pela interface mínima                                             */
/* -------------------------------------------------------------------------- */

export interface VitalHealFlashScene {
  readonly textures: { exists(key: string): boolean };
  readonly tweens: {
    add(config: Record<string, unknown>): { remove(): void };
  };
  make: {
    graphics(config?: unknown, addToScene?: boolean): VitalHealFlashGraphics;
  };
  add: {
    image(x: number, y: number, key: string): VitalHealFlashSprite;
  };
}

export interface VitalHealFlashGraphics {
  fillStyle(color: number, alpha?: number): VitalHealFlashGraphics;
  fillRect(x: number, y: number, width: number, height: number): VitalHealFlashGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

export interface VitalHealFlashSprite {
  x: number;
  y: number;
  rotation: number;
  alpha: number;
  setDepth(value: number): VitalHealFlashSprite;
  setScale(x: number, y?: number): VitalHealFlashSprite;
  setDisplaySize(width: number, height: number): VitalHealFlashSprite;
  setVisible(value: boolean): VitalHealFlashSprite;
  setTexture(key: string): VitalHealFlashSprite;
  setTintFill(color: number): VitalHealFlashSprite;
  setOrigin(x: number, y?: number): VitalHealFlashSprite;
  setBlendMode(mode: string | number): VitalHealFlashSprite;
  destroy(): void;
}

/** A textura da tira, criada uma vez por jogo e reutilizada para sempre. */
export const VITAL_BEAM_TEXTURE = 'vital-heal-beam';

/** A textura do HALO do feixe. Verde puro, sem núcleo, criada uma vez. */
export const VITAL_BEAM_GLOW_TEXTURE = 'vital-heal-beam-glow';

/**
 * A tira: bandas horizontais, do núcleo para fora, simétricas.
 *
 * `textures.exists` antes de desenhar, porque uma cena reiniciada partilha o
 * gestor de texturas do jogo: regenerar seria pagar o mesmo desenho a cada
 * abertura de arena.
 */
function ensureBeamTexture(scene: VitalHealFlashScene): void {
  if (scene.textures.exists(VITAL_BEAM_TEXTURE)) {
    return;
  }
  /*
   * **Pintada COLUNA a coluna, e não em faixas inteiras.**
   *
   * É o que separa um feixe de uma linha: cada coluna leva o alpha da sua banda
   * multiplicado pelo perfil ao comprimento, portanto as pontas afilam e os nós
   * aparecem. Em segmentos de quatro píxeis e não de um - vinte e quatro passos
   * chegam para a curva ler, e mil e tal retângulos não.
   *
   * Pago UMA vez por jogo: a textura fica no gestor e nenhuma abertura de arena
   * a redesenha.
   */
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const height = BEAM_BANDS.length * 2 - 1;
  const middle = BEAM_BANDS.length - 1;
  const step = 4;
  for (let band = 0; band < BEAM_BANDS.length; band += 1) {
    const entry = BEAM_BANDS[band];
    if (entry === undefined) {
      continue;
    }
    for (let x = 0; x < BEAM_TEXTURE_LENGTH; x += step) {
      const t = (x + step / 2) / BEAM_TEXTURE_LENGTH;
      const alpha = entry.alpha * beamLengthShape(t);
      if (alpha <= 0.01) {
        continue;
      }
      graphics.fillStyle(entry.color, alpha);
      /* A banda de cima e a de baixo. No centro (`band === 0`) são a mesma. */
      graphics.fillRect(x, middle - band, step, 1);
      if (band > 0) {
        graphics.fillRect(x, middle + band, step, 1);
      }
    }
  }
  graphics.generateTexture(VITAL_BEAM_TEXTURE, BEAM_TEXTURE_LENGTH, height);
  graphics.destroy();

  /*
   * **O HALO: verde puro, sem núcleo, e muito fraco.**
   *
   * Uma segunda textura e não a mesma esticada: se fosse a mesma, o núcleo
   * quase branco seria esticado com ela e o feixe voltaria a saturar para
   * branco - que foi exatamente o defeito da primeira versão.
   *
   * Aqui não há uma única linha branca. O que ela acrescenta é volume verde à
   * volta do fio, e ela apaga primeiro.
   */
  const halo = scene.make.graphics({ x: 0, y: 0 }, false);
  const haloRows = GLOW_BANDS.length * 2 - 1;
  const haloMiddle = GLOW_BANDS.length - 1;
  for (let band = 0; band < GLOW_BANDS.length; band += 1) {
    const alpha = GLOW_BANDS[band];
    if (alpha === undefined) {
      continue;
    }
    for (let x = 0; x < BEAM_TEXTURE_LENGTH; x += step) {
      const t = (x + step / 2) / BEAM_TEXTURE_LENGTH;
      const value = alpha * beamLengthShape(t);
      if (value <= 0.005) {
        continue;
      }
      halo.fillStyle(band === 0 ? VITAL_NEON_GREEN : VITAL_DEEP_GREEN, value);
      halo.fillRect(x, haloMiddle - band, step, 1);
      if (band > 0) {
        halo.fillRect(x, haloMiddle + band, step, 1);
      }
    }
  }
  halo.generateTexture(VITAL_BEAM_GLOW_TEXTURE, BEAM_TEXTURE_LENGTH, haloRows);
  halo.destroy();
}

/* -------------------------------------------------------------------------- */
/*  O que a cena entrega                                                      */
/* -------------------------------------------------------------------------- */

/** Uma silhueta a acender: a textura do casco, com a sua transformação. */
export interface VitalGlowTarget {
  /** O casco CURADO. Identidade do slot, e nunca uma posição. */
  readonly id: string;
  /**
   * A textura DO CASCO, lida da imagem que a cena já desenha.
   *
   * É isto que faz o brilho ter a forma da nave em vez de uma forma inventada.
   * Este módulo não sabe o que a chave nomeia e não escolhe nenhuma.
   */
  readonly textureKey: string;
  readonly x: number;
  readonly y: number;
  readonly rotation: number;
  /** O tamanho DESENHADO do casco, em píxeis da arena. A escala sai daqui. */
  readonly width: number;
  readonly height: number;
}

/** Um feixe: dois cascos com os seus raios, e a identidade do par. */
export interface VitalBeamShot {
  /** `curador:curado`. Um segundo feixe do mesmo par reinicia este. */
  readonly id: string;
  readonly fromX: number;
  readonly fromY: number;
  /** O raio DESENHADO do curador, para o feixe sair da borda dele. */
  readonly fromRadius: number;
  readonly toX: number;
  readonly toY: number;
  /** O raio DESENHADO do curado, para o feixe acabar na borda dele. */
  readonly toRadius: number;
}

/* -------------------------------------------------------------------------- */
/*  O sistema                                                                 */
/* -------------------------------------------------------------------------- */

interface LiveGlow {
  readonly core: VitalHealFlashSprite;
  readonly halo: VitalHealFlashSprite;
  readonly tweens: { remove(): void }[];
  id: string;
  active: boolean;
}

interface LiveBeam {
  readonly strip: VitalHealFlashSprite;
  readonly halo: VitalHealFlashSprite;
  readonly tweens: { remove(): void }[];
  id: string;
  active: boolean;
}

/**
 * **O sistema.** Um por cena, dono de tudo o que cria.
 *
 * Nada aqui é global e nada sobrevive a `destroy()`: os sprites são deste
 * sistema, os tweens são registados e removidos, e a textura da tira fica no
 * gestor de texturas do jogo, que é quem a descarta.
 */
export class VitalHealFlashSystem {
  private readonly glows: LiveGlow[] = [];
  private readonly beams: LiveBeam[] = [];
  private disposed = false;

  constructor(
    private readonly scene: VitalHealFlashScene,
    /** Sobre o casco e por baixo da barra e do marcador. A cena decide. */
    private readonly glowDepth: number,
    /** Por cima dos cascos e por baixo das barras. */
    private readonly beamDepth: number,
  ) {
    ensureBeamTexture(scene);
    this.prewarm();
  }

  /**
   * Cria os slots antes de alguém precisar deles.
   *
   * Nada aqui é desenhado: os sprites nascem invisíveis e inativos, e o primeiro
   * `glow` ou `beam` encontra-os pelo mesmo caminho que encontraria um slot
   * reciclado. O efeito é idêntico ao milissegundo - o que muda é QUANDO a
   * memória é pedida.
   */
  private prewarm(): void {
    for (let slot = 0; slot < VITAL_PREWARMED_SLOTS; slot += 1) {
      this.glows.push({
        core: this.blankSprite(),
        halo: this.blankSprite(),
        tweens: [],
        id: '',
        active: false,
      });
      this.beams.push(this.blankBeam());
    }
  }

  /**
   * Acende a silhueta do casco CURADO.
   *
   * Idempotência é responsabilidade de quem entrega: a fila da ligação garante
   * que um evento chega uma vez. O que ESTE método garante é que um casco tem um
   * brilho - uma segunda chamada dentro dos 120 ms reinicia o dele.
   */
  glow(target: VitalGlowTarget): void {
    if (this.disposed) {
      return;
    }
    const live = this.takeGlow(target.id);
    live.id = target.id;
    live.active = true;

    for (const sprite of [live.core, live.halo]) {
      sprite.setTexture(target.textureKey);
      /*
       * **A COR substituída, o alpha preservado.**
       *
       * `setTintFill` e nunca `setTint`: o segundo MULTIPLICA, portanto um verde
       * sobre uma nave escura continuaria escuro. Este substitui, e o que sobra é
       * a silhueta exata, sólida e verde.
       */
      sprite.setTintFill(VITAL_NEON_GREEN);
      /*
       * Aditivo, que é o que faz "neon" ser neon: a luz SOMA-SE ao que está por
       * baixo em vez de o tapar, portanto a nave continua a ler-se através do
       * brilho e a identidade dela sobrevive aos 120 ms.
       */
      sprite.setBlendMode('ADD');
      sprite.x = target.x;
      sprite.y = target.y;
      sprite.rotation = target.rotation;
      sprite.setVisible(true);
    }
    live.core.setDisplaySize(target.width, target.height);
    live.core.setDepth(this.glowDepth + 0.5);
    live.halo.setDepth(this.glowDepth - 0.5);

    /* O NÚCLEO: sobe quase instantaneamente e apaga sobre a silhueta exata. */
    live.core.alpha = 0;
    this.pushGlow(live, {
      targets: live.core,
      alpha: VITAL_GLOW_CORE_ALPHA,
      duration: VITAL_GLOW_PEAK_MS,
      ease: 'Quad.easeOut',
    });
    this.pushGlow(live, {
      targets: live.core,
      alpha: 0,
      delay: VITAL_GLOW_PEAK_MS,
      duration: VITAL_GLOW_DURATION_MS - VITAL_GLOW_PEAK_MS,
      ease: 'Quad.easeIn',
    });

    /*
     * O HALO: nasce 16 % MAIOR e ENCOLHE para a silhueta enquanto apaga.
     *
     * Ao contrário da Égide, que cresce. É esta inversão que faz o efeito ler
     * como energia a entrar - uma recuperação - em vez de energia a sair. Ver a
     * nota de {@link VITAL_GLOW_HALO_START_SCALE}.
     */
    live.halo.setDisplaySize(
      target.width * VITAL_GLOW_HALO_START_SCALE,
      target.height * VITAL_GLOW_HALO_START_SCALE,
    );
    live.halo.alpha = VITAL_GLOW_HALO_ALPHA;
    this.pushGlow(live, {
      targets: live.halo,
      displayWidth: target.width,
      displayHeight: target.height,
      alpha: 0,
      duration: VITAL_GLOW_DURATION_MS,
      ease: 'Quad.easeOut',
      /* O slot volta ao pool no MESMO instante em que o último pixel apaga. */
      onComplete: () => this.releaseGlow(live),
    });
  }

  /**
   * Desenha o feixe entre as BORDAS de dois cascos.
   *
   * Inteiro no primeiro quadro, e nunca a viajar: a cura já foi aplicada, e
   * animar uma travessia seria desenhar uma viagem que a simulação não fez.
   *
   * Silencioso quando não há segmento honesto - ver {@link vitalBeamSegment}.
   */
  beam(shot: VitalBeamShot): void {
    if (this.disposed) {
      return;
    }
    const segment = vitalBeamSegment(shot);
    if (segment === null) {
      return;
    }
    const live = this.takeBeam(shot.id);
    live.id = shot.id;
    live.active = true;

    /*
     * **Duas camadas, e o halo apaga PRIMEIRO.**
     *
     * O núcleo é o fio e o halo é o volume à volta dele. Fazê-los apagar juntos
     * daria um traço a desvanecer; fazer o halo ir-se primeiro deixa o fio
     * sozinho no fim, que é como uma descarga acaba.
     */
    for (const layer of [live.halo, live.strip]) {
      /*
       * A ORIGEM na ponta esquerda e no meio da altura: a tira cresce a partir
       * da borda do curador na direção do curado, e roda em torno do ponto de
       * partida.
       */
      layer.setOrigin(0, 0.5);
      layer.setBlendMode('ADD');
      layer.x = segment.fromX;
      layer.y = segment.fromY;
      layer.rotation = segment.rotation;
      layer.setVisible(true);
      layer.alpha = 0;
    }
    live.strip.setDisplaySize(segment.length, VITAL_BEAM_THICKNESS_PX);
    live.halo.setDisplaySize(segment.length, VITAL_BEAM_GLOW_THICKNESS_PX);

    /*
     * Uma subida muito curta e uma queda. Sem crescimento, sem encolhimento e
     * sem deslocação: o feixe está lá e apaga. Uma linha que se expandisse leria
     * como uma onda de choque, que é outro efeito e não este.
     */
    this.pushBeam(live, {
      targets: live.strip,
      alpha: 1,
      duration: VITAL_BEAM_PEAK_MS,
      ease: 'Quad.easeOut',
    });
    this.pushBeam(live, {
      targets: live.strip,
      alpha: 0,
      delay: VITAL_BEAM_PEAK_MS,
      duration: VITAL_BEAM_DURATION_MS - VITAL_BEAM_PEAK_MS,
      ease: 'Quad.easeIn',
      onComplete: () => this.releaseBeam(live),
    });
    this.pushBeam(live, {
      targets: live.halo,
      alpha: 1,
      duration: VITAL_BEAM_PEAK_MS,
      ease: 'Quad.easeOut',
    });
    this.pushBeam(live, {
      targets: live.halo,
      alpha: 0,
      delay: VITAL_BEAM_PEAK_MS,
      /* Dois terços do núcleo: o volume vai-se e o fio fica. */
      duration: Math.round((VITAL_BEAM_DURATION_MS - VITAL_BEAM_PEAK_MS) * 0.66),
      ease: 'Quad.easeIn',
    });
  }

  /** Quantos brilhos estão vivos agora. Para as provas e para as medições. */
  liveGlowCount(): number {
    return this.glows.filter((entry) => entry.active).length;
  }

  /** Quantos feixes estão vivos agora. */
  liveBeamCount(): number {
    return this.beams.filter((entry) => entry.active).length;
  }

  /** Quantos objetos o sistema alocou ao todo. Nunca deve crescer sem uso. */
  pooledCount(): number {
    return this.glows.length + this.beams.length;
  }

  /**
   * Apaga tudo o que está no ecrã, sem largar os objetos.
   *
   * Para uma CORREÇÃO, uma morte, uma reconexão ou uma substituição de mundo: o
   * que estava a ser desenhado pertencia a um estado que já não existe.
   */
  clear(): void {
    for (const live of this.glows) {
      this.releaseGlow(live);
    }
    for (const live of this.beams) {
      this.releaseBeam(live);
    }
  }

  /**
   * Larga tudo. Depois disto o sistema não desenha mais nada.
   *
   * Os tweens são removidos ANTES dos sprites: um tween cujo alvo foi destruído
   * continua a correr e a escrever num objeto morto.
   */
  destroy(): void {
    this.disposed = true;
    for (const live of this.glows) {
      for (const tween of live.tweens) {
        tween.remove();
      }
      live.tweens.length = 0;
      live.core.destroy();
      live.halo.destroy();
    }
    this.glows.length = 0;
    for (const live of this.beams) {
      for (const tween of live.tweens) {
        tween.remove();
      }
      live.tweens.length = 0;
      live.strip.destroy();
      live.halo.destroy();
    }
    this.beams.length = 0;
  }

  private pushGlow(live: LiveGlow, config: Record<string, unknown>): void {
    live.tweens.push(this.scene.tweens.add(config));
  }

  private pushBeam(live: LiveBeam, config: Record<string, unknown>): void {
    live.tweens.push(this.scene.tweens.add(config));
  }

  private releaseGlow(live: LiveGlow): void {
    for (const tween of live.tweens) {
      tween.remove();
    }
    live.tweens.length = 0;
    live.active = false;
    live.core.setVisible(false);
    live.halo.setVisible(false);
  }

  private releaseBeam(live: LiveBeam): void {
    for (const tween of live.tweens) {
      tween.remove();
    }
    live.tweens.length = 0;
    live.active = false;
    live.strip.setVisible(false);
    live.halo.setVisible(false);
  }

  /**
   * O slot deste CASCO, e é sempre o mesmo.
   *
   * A identidade vem primeiro: um casco que já está a brilhar reinicia o seu
   * brilho em vez de acender um segundo. Só depois se procura um livre, e só
   * depois se cria - e o teto nunca é alcançado por construção, porque há
   * exatamente um slot por casco possível.
   */
  private takeGlow(id: string): LiveGlow {
    const mine = this.glows.find((entry) => entry.active && entry.id === id);
    if (mine) {
      this.releaseGlow(mine);
      return mine;
    }
    const free = this.glows.find((entry) => !entry.active);
    if (free) {
      return free;
    }
    const created: LiveGlow = {
      core: this.blankSprite(),
      halo: this.blankSprite(),
      tweens: [],
      id,
      active: false,
    };
    this.glows.push(created);
    return created;
  }

  /** O slot deste PAR, pela mesma regra exata. */
  private takeBeam(id: string): LiveBeam {
    const mine = this.beams.find((entry) => entry.active && entry.id === id);
    if (mine) {
      this.releaseBeam(mine);
      return mine;
    }
    const free = this.beams.find((entry) => !entry.active);
    if (free) {
      return free;
    }
    const created = this.blankBeam();
    created.id = id;
    this.beams.push(created);
    return created;
  }

  /**
   * Um sprite sem textura própria: ela é atribuída a cada brilho.
   *
   * Nasce com a da tira porque `add.image` precisa de uma chave que exista, e
   * ela é substituída pela do casco na primeira utilização. Nunca é DESENHADA
   * assim: o sprite nasce invisível.
   */
  /** Um slot de feixe por estrear: as duas camadas, invisíveis. */
  private blankBeam(): LiveBeam {
    const strip = this.scene.add.image(0, 0, VITAL_BEAM_TEXTURE);
    strip.setDepth(this.beamDepth).setVisible(false);
    /* O halo POR BAIXO do núcleo, meio degrau abaixo: ele é o volume, não o fio. */
    const halo = this.scene.add.image(0, 0, VITAL_BEAM_GLOW_TEXTURE);
    halo.setDepth(this.beamDepth - 0.5).setVisible(false);
    return { strip, halo, tweens: [], id: '', active: false };
  }

  private blankSprite(): VitalHealFlashSprite {
    const sprite = this.scene.add.image(0, 0, VITAL_BEAM_TEXTURE);
    sprite.setDepth(this.glowDepth).setVisible(false);
    return sprite;
  }
}
